import React, { useEffect, useState } from 'react';
import { Edit, Plus, Search, Calendar, User, Car, Eye, Clock, Trash2 } from 'lucide-react';
import axios from '../api/axios';
import toast from 'react-hot-toast';
import JobCardForm from './jobcards/JobCardForm';
import EditJobCardForm from './jobcards/EditJobCardForm';
import JobCardStatusUpdate from './jobcards/JobCardStatusUpdate';
import ViewJobCard from './jobcards/ViewJobCard';

import AuthCheck from './AuthCheck';
import { getStatusColor } from '../utils/jobCardStatus';
import { usePermissions } from '../hooks/usePermissions';
import { formatDate, formatTime } from '../utils/dateFormat';

const JobCards = () => {
  const { hasPermission } = usePermissions();
  const [jobCards, setJobCards] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showStatusUpdate, setShowStatusUpdate] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingJobCard, setEditingJobCard] = useState(null);
  const [updatingJobCard, setUpdatingJobCard] = useState(null);
  const [viewingJobCard, setViewingJobCard] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    startDate: '',
    endDate: ''
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchJobCards();
  }, [filters]);

  const fetchJobCards = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key]) params.append(key, filters[key]);
      });

      const response = await axios.get(`/api/job-cards?${params}`);
      const jobCardsData = response.data.jobCards || response.data.data || response.data;
      setJobCards(Array.isArray(jobCardsData) ? jobCardsData : []);
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message || 'Failed to fetch job cards';
      toast.error(errorMessage);
      if (error.response?.status === 401) {
        toast.error('Please login to access job cards');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    fetchJobCards();
  };

  const handleEditFormSuccess = () => {
    setShowEditForm(false);
    setEditingJobCard(null);
    fetchJobCards();
  };

  const handleStatusUpdateSuccess = () => {
    setShowStatusUpdate(false);
    setUpdatingJobCard(null);
    fetchJobCards();
  };

  const handleEdit = (jobCard) => {
    setEditingJobCard(jobCard);
    setShowEditForm(true);
  };

  const handleStatusUpdate = (jobCard) => {
    setUpdatingJobCard(jobCard);
    setShowStatusUpdate(true);
  };

  const handleView = (jobCard) => {
    setViewingJobCard(jobCard);
    setShowViewModal(true);
  };

  const handleDelete = async (jobCard) => {
    if (!window.confirm(`Are you sure you want to delete job card ${jobCard.orderNo}? This action cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`/api/job-cards/${jobCard.id}`);
      toast.success('Job card deleted successfully');
      fetchJobCards();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete job card');
    }
  };





  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Job Cards</h1>
        {hasPermission('jobcards.create') && (
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 w-full sm:w-auto justify-center touch-target"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Job Card
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white p-3 sm:p-4 rounded-lg shadow border">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <div className="relative sm:col-span-2 lg:col-span-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search order no, customer..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="pl-10 w-full px-3 py-3 sm:py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
            />
          </div>

          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="px-3 py-3 sm:py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
          >
            <option value="">All Status</option>
            <option value="PENDING">Pending</option>
            <option value="WAITING_FOR_PARTS">Waiting for Parts</option>
            <option value="IN_PROGRESS">In Progress</option>
            <option value="COMPLETED">Completed</option>
            <option value="CANCELLED">Cancelled</option>
          </select>

          <input
            type="date"
            value={filters.startDate}
            onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
            className="px-3 py-3 sm:py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
          />

          <input
            type="date"
            value={filters.endDate}
            onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
            className="px-3 py-3 sm:py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
          />
        </div>
      </div>

      {/* Job Cards Table */}
      <div className="bg-white rounded-lg shadow border overflow-hidden">
        <div className="overflow-x-auto responsive-table-wrapper">
          <table className="min-w-full divide-y divide-gray-200 responsive-table">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order No</th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">Date/Time</th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Vehicle</th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden lg:table-cell">Technician</th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <tr key={index}>
                    <td colSpan="7" className="px-6 py-4">
                      <div className="animate-pulse h-4 bg-gray-200 rounded"></div>
                    </td>
                  </tr>
                ))
              ) : jobCards.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                    No job cards found
                  </td>
                </tr>
              ) : (
                (jobCards || []).map((jobCard) => (
                  <tr key={jobCard.id} className="hover:bg-gray-50">
                    <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm font-medium text-blue-600">
                      {jobCard.orderNo}
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm text-gray-900 hidden sm:table-cell">
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-1 text-gray-400" />
                        <div>
                          <div>{formatDate(jobCard.appointmentDate) || '-'}</div>
                          <div className="text-xs text-gray-500">{formatTime(jobCard.appointmentDate) || '-'}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm text-gray-900">
                      <div className="flex items-center">
                        <User className="w-4 h-4 mr-1 text-gray-400 hidden sm:inline" />
                        <div>
                          <div className="truncate max-w-[120px] sm:max-w-none">{jobCard.customer?.name || 'N/A'}</div>
                          <div className="text-xs text-gray-500 hidden sm:block">{jobCard.customer?.contact || 'N/A'}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm text-gray-900 hidden md:table-cell">
                      {jobCard.vehicle ? (
                        <div className="flex items-center">
                          <Car className="w-4 h-4 mr-1 text-gray-400" />
                          <div>
                            <div>{jobCard.vehicle.brand?.name || jobCard.vehicle.make || 'N/A'}</div>
                            <div className="text-xs text-gray-500">{jobCard.vehicle.model?.name || jobCard.vehicle.model || 'N/A'}</div>
                            {jobCard.vehicleNumber && (
                              <div className="text-xs text-gray-500">No: {jobCard.vehicleNumber}</div>
                            )}
                            {jobCard.vinNumber && (
                              <div className="text-xs text-gray-500">VIN: {jobCard.vinNumber}</div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <span className="text-gray-400">No vehicle</span>
                      )}
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm text-gray-900 hidden lg:table-cell">
                      {jobCard.technician?.name || 'Not assigned'}
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(jobCard.status)}`}>
                        {jobCard.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4">
                      <div className="flex space-x-1 sm:space-x-2">
                        <button
                          onClick={() => handleView(jobCard)}
                          className="text-blue-600 hover:text-blue-800"
                          title="View Job Card"
                        >
                          👁️
                        </button>
                        {hasPermission('jobcards.approve') && (
                          <button
                            onClick={() => handleStatusUpdate(jobCard)}
                            className="text-green-600 hover:text-green-800"
                            title="Update Status"
                          >
                            🔄
                          </button>
                        )}
                        {hasPermission('jobcards.update') && (
                          <button
                            onClick={() => handleEdit(jobCard)}
                            className="text-blue-600 hover:text-blue-800"
                            title="Edit Job Card"
                          >
                            ✏️
                          </button>
                        )}
                        {hasPermission('jobcards.delete') && (
                          <button
                            onClick={() => handleDelete(jobCard)}
                            className="text-red-600 hover:text-red-800"
                            title="Delete Job Card"
                          >
                            🗑️
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Job Card Form Modal */}
      {showForm && (
        <JobCardForm
          onClose={() => setShowForm(false)}
          onSuccess={handleFormSuccess}
        />
      )}

      {/* Edit Job Card Form Modal */}
      {showEditForm && editingJobCard && (
        <EditJobCardForm
          jobCard={editingJobCard}
          onClose={() => {
            setShowEditForm(false);
            setEditingJobCard(null);
          }}
          onSuccess={handleEditFormSuccess}
        />
      )}

      {/* Status Update Modal */}
      {showStatusUpdate && updatingJobCard && (
        <JobCardStatusUpdate
          jobCard={updatingJobCard}
          onClose={() => {
            setShowStatusUpdate(false);
            setUpdatingJobCard(null);
          }}
          onSuccess={handleStatusUpdateSuccess}
        />
      )}

      {/* View Job Card Modal */}
      {showViewModal && viewingJobCard && (
        <ViewJobCard
          jobCard={viewingJobCard}
          onClose={() => {
            setShowViewModal(false);
            setViewingJobCard(null);
          }}
        />
      )}
    </div>
  );
};

const JobCardsWithAuth = () => (
  <AuthCheck>
    <JobCards />
  </AuthCheck>
);

export default JobCardsWithAuth;
